import * as d3 from 'd3';
import ndarray from 'ndarray';
import interp from 'ndarray-linear-interpolate';
import { createSelectorCreator, createSelector } from 'reselect';
import memoize from 'lodash.memoize';
import uniq from 'lodash.uniq';
import { ASSET_INFLATION_SENSITIVITY, LIABILITY_INFLATION_SENSITIVITY, FUNDING_LEVEL_GAIN, INFLATION_HEDGE_EFFECTIVENESS, RATES_HEDGE_EFFECTIVENESS } from './Dashboard';

const unboundedCacheSelectorCreator = createSelectorCreator(memoize);

const getVolatilities = (state) => {
  return uniq(state.map(d => d.InfVol).sort((a, b) => a - b));
}

const getInflationAdjustments = (state) => {
  return uniq(state.map(d => d.InfAdj).sort((a, b) => a - b));
}

// Create a 2d array and cache it for later use
export const get2dData = createSelector(
  state => state.inflationSurface,
  state => state.property,
  (inflationSurface, property) => {
    const volatilities = uniq(inflationSurface.map(x => x.InfVol)).sort((a, b) => a - b);
    const inflationAdjustments = uniq(inflationSurface.map(d => d.InfAdj)).sort((a, b) => a - b);

    const nVol = volatilities.length;
    const nAdj = inflationAdjustments.length;
    const array = ndarray(new Float32Array(nVol * nAdj), [nVol, nAdj]);

    for (let i = 0; i < inflationSurface.length; i++) {
      const element = inflationSurface[i];
      const iVol = volatilities.indexOf(element['InfVol']);
      const iAdj = inflationAdjustments.indexOf(element['InfAdj']);
      array.set(iVol, iAdj, element[property]);
    }

    return array;
  }
);

export const get2dCharacteristics = unboundedCacheSelectorCreator(
  [state => state],
  (state) => {
    const volatilities = uniq(state.map(x => x.InfVol)).sort((a, b) => a - b);
    const inflationAdjustments = uniq(state.map(d => d.InfAdj)).sort((a, b) => a - b);

    const nVol = volatilities.length;
    const nInf = inflationAdjustments.length;
    const array = ndarray(new Float32Array(nVol * nInf), [nVol, nInf]);

    for (let i = 0; i < state.length; i++) {
      const element = state[i];
      const iVol = volatilities.indexOf(element['InfVol']);
      const iAdj = inflationAdjustments.indexOf(element['InfAdj']);
      array.set(iVol, iAdj, element.Value);
    }

    return array;
  }
);

const getAll2dCharacteristics = createSelector(
  state => state.ldiCharacteristics,
  state => state.items,
  (ldiCharacteristics, items) => {
    const itemsMap = {
      Price: ['Price Real', 'Price Fixed'],
      Delta: ['IE01', 'PV01'],
      Sensitivity: ['Inflation Sensitivity'],
    };

    const itemsByDate = d3.nest().key(d => d['Date Paid']).entries(ldiCharacteristics);
    const groupedItems = itemsMap[items].map(d => itemsByDate.map(x => x.values.map(y => ({ Value: y[d], InfVol: y.InfVol, InfAdj: y.InfAdj, 'Date Paid': y['Date Paid'] }))));

    return groupedItems.map(d => d.map(get2dCharacteristics));
  }
)

export const getInitialVol = (inflationSurface) => {
  const volatilities = getVolatilities(inflationSurface)
  return volatilities[Math.floor(volatilities.length / 2)];
}

const getExtent = (arrays, volatilities, inflationAdjustments, selected) => {
  let max = -Infinity;
  let min = Infinity;

  for (const [key, value] of Object.entries(arrays)) {
    if (selected.includes(key)) {
      for (let i = 0; i < volatilities.length; i++) {
        for (let j = 0; j < inflationAdjustments.length; j++) {
          const val = interp(value, i, j);

          max = val > max ? val : max;
          min = val < min ? val : min;
        }
      }
    }
  }

  return [min, max];
}

export const getThreshold = (inflationSurface, ldiCharacteristics, surfaces, view) => {
  const surface = surfaces[view];

  const volatilities = getVolatilities(inflationSurface);
  const inflationAdjustments = getInflationAdjustments(inflationSurface);

  // Value at centre of surface
  return interp(surface, (volatilities.length - 1) / 2, (inflationAdjustments.length - 1) / 2);
}

export const getInflationCurveChartData = (inflationCurve, inflationSurface, ldiCharacteristics) => {
  return {
    data: inflationCurve.map(d => ({ term: d.Term, rate: d.Value })),
    volatilities: getVolatilities(inflationSurface),
    inflationAdjustments: getInflationAdjustments(inflationSurface),
  }
}

export const getinflationSensitivityChartData = (infAdj, vol, inflationSurface, ldiCharacteristics, surfaces, selected) => {
  const volatilities = getVolatilities(inflationSurface);
  const inflationAdjustments = getInflationAdjustments(inflationSurface);

  const u = (vol - volatilities[0]) / (volatilities[1] - volatilities[0]);
  const v = (infAdj - inflationAdjustments[0]) / (inflationAdjustments[1] - inflationAdjustments[0]);

  const output = {};
  const pointValues = {};

  for (const [key, value] of Object.entries(surfaces)) {
    pointValues[key] = interp(surfaces[key], u, v);

    if (selected.includes(key)) {
      output[key] = [];
      for (let iInf = 0; iInf < inflationAdjustments.length; iInf++) {
        output[key].push({ x: inflationAdjustments[iInf], sensitivity: interp(value, u, iInf) })
      }
    }
  }

  return {
    data: output,
    value: pointValues,
    extent: getExtent(surfaces, volatilities, inflationAdjustments, selected),
  }
}

export const getVolatilitySensitivityChartData = (infAdj, vol, inflationSurface, ldiCharacteristics, surfaces, selected) => {
  const volatilities = getVolatilities(inflationSurface);
  const inflationAdjustments = getInflationAdjustments(inflationSurface);

  const v = (infAdj - inflationAdjustments[0]) / (inflationAdjustments[1] - inflationAdjustments[0]);

  const output = {};

  for (const [key, value] of Object.entries(surfaces)) {
    if (selected.includes(key)) {
      output[key] = volatilities.map((vol, index) => ({ x: vol, sensitivity: interp(value, index, v) }));
    }
  }

  return {
    data: output,
    extent: getExtent(surfaces, volatilities, inflationAdjustments, selected),
  }
}

export const getPriceChartData = (inflationSurface, ldiCharacteristics, vol, infAdj, items) => {
  const volatilities = getVolatilities(inflationSurface);
  const inflationAdjustments = getInflationAdjustments(inflationSurface);

  const u = (vol - volatilities[0]) / (volatilities[1] - volatilities[0]);
  const v = (infAdj - inflationAdjustments[0]) / (inflationAdjustments[1] - inflationAdjustments[0]);

  const surfaces = getAll2dCharacteristics({ ldiCharacteristics, items });
  const arrays = surfaces.map(d => d.map(x => interp(x, u, v)).map((x, i) => ({ term: i, value: x })));

  let yExtent;

  switch (items) {
    case 'Price':
      yExtent = [0, d3.max(arrays.map(x => d3.max(x.map(d => d.value))))] // Max over both arrays
      break;
    case 'Delta':
      yExtent = [0, d3.max(arrays[0].map((x, i) => x.value + arrays[1][i].value))]; // Sum both arrays and calculate max
      break;
    case 'Sensitivity':
      yExtent = [0, 1]; // Percentage
      break;
    default:
      console.log(`Unknown price chart data: ${items}`)
  }

  return { data: arrays, yExtent: yExtent };
}

const getAll2dCharacteristicsForKeyFiguresTableData = createSelector(
  ldiCharacteristics => ldiCharacteristics,
  (ldiCharacteristics) => {
    const items = ['Price Real',
      'Price Fixed',
      'IE01',
      'PV01',
      'Inflation Sensitivity',
    ];

    const itemsByDate = d3.nest().key(d => d['Date Paid']).entries(ldiCharacteristics);
    const groupedItems = items.map(d => itemsByDate.map(x => x.values.map(y => ({ Value: y[d], InfVol: y.InfVol, InfAdj: y.InfAdj, 'Date Paid': y['Date Paid'] }))));

    return groupedItems.map(d => d.map(get2dCharacteristics));
  }
)

export const getKeyFiguresTableData = (inflationSurface, ldiCharacteristics, vol, infAdj, items, value) => {
  const volatilities = getVolatilities(inflationSurface);
  const inflationAdjustments = getInflationAdjustments(inflationSurface);

  const u = (vol - volatilities[0]) / (volatilities[1] - volatilities[0]);
  const v = (infAdj - inflationAdjustments[0]) / (inflationAdjustments[1] - inflationAdjustments[0]);

  const surfaces = getAll2dCharacteristicsForKeyFiguresTableData(ldiCharacteristics);
  const totals = surfaces.map(d => d.map(x => interp(x, u, v)).reduce((acc, val) => acc + val, 0));

  const format = d3.format(',.0f');
  const formatPercentage = d3.format('.0%');
  const formatSignPercentage = d3.format('+.0%');

  return {
    'Gain/Loss':
    [
      { id: 'Funding Level Gain', value: formatSignPercentage(value[FUNDING_LEVEL_GAIN]) },
    ],
    'New hedge': [
      { id: 'Total Price Real', value: format(totals[0]) },
      { id: 'Total Price Fixed', value: format(totals[1]) },
      { id: 'Total IE01', value: format(totals[2]) },
      { id: 'Total PV01', value: format(totals[3]) },
    ],
    'Initial solution accuracy':
    [
      { id: 'Inflation Sensitivity', value: formatSignPercentage(value[ASSET_INFLATION_SENSITIVITY] - value[LIABILITY_INFLATION_SENSITIVITY]) },
      { id: 'Inflation Hedge Effectiveness', value: formatPercentage(value[INFLATION_HEDGE_EFFECTIVENESS]) },
      { id: 'Rates Hedge Effectiveness', value: formatPercentage(value[RATES_HEDGE_EFFECTIVENESS]) },
    ],
  }
}


// WEBPACK FOOTER //
// ./src/dashboards/InvestibleBenchmark/selectors.js